import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check, CheckCircle2, Circle, Clock, ExternalLink, FileDown, FileText, Loader2, Plus, RefreshCw, Trash2, Upload, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  GraphState,
  ProductGraph,
} from "@/services/platformEnterprisesService";
import {
  getNodeById,
  getMaxActors,
  getOutgoingEdges,
  getActorLabel,
  type DecisionNodeData,
  type InternalRequestNodeData,
  type InviteActorsNodeData,
  type PreconditionNodeData,
  type PricingNodeData,
  type StaffNodeData,
  type UserFormNodeData,
  type WizardFieldDef,
} from "@/lib/productGraph";
import { type DocumentCollectionNodeData } from "@gestionalo/enterprise-graph";
import { evaluateRateTable } from "@/utils/enterprisePricingEngine";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { WizardStepHeading } from "@/components/shared/WizardStepHeading";
import { InternalRequestNodeView } from "@/components/features/enterprise/InternalRequestNodeView";
import { InviteActorsNodeView } from "@/components/features/enterprise/InviteActorsNodeView";
import { useAuth } from "@/contexts/AuthContext";
import { getEnterpriseRequestParticipants, resendParticipantInvite, cancelParticipantInvite, fetchMyEnterpriseDocumentBlob, uploadMyCollectionDocument, completeMyEnterpriseRequestStep } from "@/services/enterpriseCatalogService";
import { AuthenticatedBlobDocumentLink } from "@/components/shared/AuthenticatedBlobDocumentLink";

interface Props {
  graph: ProductGraph;
  state: GraphState;
  enterpriseRequestId: string;
  allPayloadValues: Record<string, unknown>;
  onCompleteNode: (nodeId: string, data: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
  /** Hide the bottom back/cancel button (useful when embedded in a detail page with its own navigation) */
  hideBackButton?: boolean;
  /** Hide the workflow stepper (useful when embedded in a wizard with its own navigation) */
  hideStepper?: boolean;
  /** Hide the step heading (useful when the parent renders its own heading) */
  hideHeading?: boolean;
  /** When set, filters the stepper to only show steps relevant to this participant (by inviteNodeId). */
  participantInviteNodeId?: string | null;
}

export function GraphWorkflowRunner({
  graph,
  state,
  enterpriseRequestId,
  allPayloadValues,
  onCompleteNode,
  onBack,
  hideBackButton = false,
  hideStepper = false,
  hideHeading = false,
  participantInviteNodeId,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [actorCount, setActorCount] = useState(1);

  const currentNode = state.currentNodeId
    ? getNodeById(graph, state.currentNodeId)
    : null;

  const isParticipantStep = currentNode?.type === "userForm" &&
    Boolean((currentNode?.data as { actorSource?: string } | undefined)?.actorSource);
  const participantActorSource = isParticipantStep
    ? (currentNode!.data as { actorSource?: string }).actorSource!
    : null;

  const participantsQuery = useQuery({
    queryKey: ["enterprise-request-participants", enterpriseRequestId],
    queryFn: () => getEnterpriseRequestParticipants(enterpriseRequestId),
    enabled: isParticipantStep,
  });

  const isCurrentUserParticipant = isParticipantStep && participantsQuery.data?.some(
    (p) => p.userId === user?.id && p.status === 'accepted' &&
      (p.inviteNodeId === participantActorSource || p.role === 'initiator'),
  );

  // Pre-fill form values when viewing a completed node (back navigation)
  const isViewingCompletedNode = currentNode != null && state.completedPath.includes(currentNode.id);
  useEffect(() => {
    if (!isViewingCompletedNode || !currentNode) return;
    const savedData = allPayloadValues[currentNode.id];
    if (savedData && typeof savedData === 'object' && !Array.isArray(savedData)) {
      setValues(savedData as Record<string, unknown>);
    }
  }, [isViewingCompletedNode, currentNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const INTERACTIVE_TYPES = new Set([
    "userForm", "precondition", "staffApproval", "staffAction", "internalRequest", "inviteActors", "decision", "automation", "documentCollection",
  ]);

  const steps = useMemo(() => {
    // Build an ordered list of interactive steps:
    // 1. Completed interactive nodes (from completedPath, preserving order)
    // 2. Current node (if interactive)
    // 3. Future nodes: walk forward from current via edges, collecting interactive ones
    const result: Array<{
      id: string;
      label: string;
      status: "completed" | "current" | "upcoming";
    }> = [];

    // Completed interactive steps — skip nodes before (and including) createRequest
    // since those were completed during the wizard, not on this page.
    const createRequestIdx = state.completedPath.findIndex((id) => {
      const n = getNodeById(graph, id);
      return n?.type === 'createRequest';
    });
    const visibleCompletedPath = createRequestIdx >= 0
      ? state.completedPath.slice(createRequestIdx + 1)
      : state.completedPath;

    for (const nodeId of visibleCompletedPath) {
      const node = getNodeById(graph, nodeId);
      if (!node || !INTERACTIVE_TYPES.has(node.type)) continue;
      result.push({
        id: nodeId,
        label: (node.data as { label?: string }).label ?? nodeId,
        status: "completed",
      });
    }

    // Current step
    if (state.currentNodeId) {
      const curNode = getNodeById(graph, state.currentNodeId);
      if (curNode && INTERACTIVE_TYPES.has(curNode.type)) {
        result.push({
          id: state.currentNodeId,
          label: (curNode.data as { label?: string }).label ?? state.currentNodeId,
          status: "current",
        });
      }

      // Future steps: walk forward from current node following edges
      const visited = new Set<string>([...state.completedPath, state.currentNodeId]);
      let walkId: string | null = state.currentNodeId;
      for (let safety = 0; safety < graph.nodes.length && walkId; safety++) {
        const outEdges = getOutgoingEdges(graph, walkId);
        // Follow the first (or only) outgoing edge; skip branch alternatives
        const nextEdge = outEdges.length === 1 ? outEdges[0] : null;
        if (!nextEdge || visited.has(nextEdge.target)) break;
        visited.add(nextEdge.target);
        walkId = nextEdge.target;
        const nextNode = getNodeById(graph, walkId);
        if (!nextNode) break;
        if (INTERACTIVE_TYPES.has(nextNode.type)) {
          result.push({
            id: walkId,
            label: (nextNode.data as { label?: string }).label ?? walkId,
            status: "upcoming",
          });
        }
        // If it's an end node, stop
        if (nextNode.type === "end") break;
      }
    }

    // Filter out conditional nodes that don't apply to the current user
    const filtered = result.filter((step) => {
      const node = getNodeById(graph, step.id);
      if (!node) return true;
      const cond = (node.data as { condition?: { field: string; values: string[] } }).condition;
      if (!cond) return true;
      // Look up the condition field value from nested payload (try _0 suffix first for per-actor fields)
      const keysToTry = [`${cond.field}_0`, cond.field];
      for (const key of keysToTry) {
        for (const nodePayload of Object.values(allPayloadValues)) {
          if (nodePayload && typeof nodePayload === "object" && !Array.isArray(nodePayload)) {
            const val = (nodePayload as Record<string, unknown>)[key];
            if (val != null) return cond.values.includes(String(val));
          }
        }
      }
      // If field not found yet (payload not available), keep the step visible
      return true;
    });

    // If participantInviteNodeId is set, filter to only participant-relevant steps
    if (participantInviteNodeId) {
      return filtered.filter((step) => {
        const node = getNodeById(graph, step.id);
        if (!node) return false;
        // Show the inviteActors node itself
        if (node.id === participantInviteNodeId) return true;
        // Show userForm nodes linked to this participant via actorSource
        const actorSource = (node.data as { actorSource?: string } | undefined)?.actorSource;
        if (actorSource === participantInviteNodeId) return true;
        return false;
      });
    }

    return filtered;
  }, [graph, state.completedPath, state.currentNodeId, participantInviteNodeId, allPayloadValues]);

  const progress = useMemo(() => {
    const currentIdx = steps.findIndex((s) => s.status === "current");
    return {
      current: currentIdx >= 0 ? currentIdx + 1 : steps.length,
      total: steps.length,
    };
  }, [steps]);

  // Documents from completed steps that the current user can download
  // Documents from completed steps that the current user can download
  // For limited-scope participants, only show staff-uploaded documents
  // Documents from completed steps that the current user can download.
  // Only show documents uploaded AFTER request creation (skip wizard steps),
  // and only from staff or participant nodes (not the initiator's own uploads).
  const completedDocuments = useMemo(() => {
    if (!enterpriseRequestId) return [];

    // Find createRequest node index to skip wizard-phase uploads
    const createRequestIdx = state.completedPath.findIndex((id) => {
      const n = getNodeById(graph, id);
      return n?.type === "createRequest";
    });
    const postCreationPath = createRequestIdx >= 0
      ? state.completedPath.slice(createRequestIdx + 1)
      : state.completedPath;

    const docs: Array<{ nodeId: string; label: string; fileName: string }> = [];
    for (const nodeId of postCreationPath) {
      const data = allPayloadValues[nodeId];
      if (!data || typeof data !== "object" || Array.isArray(data)) continue;
      const d = data as Record<string, unknown>;
      if (d.fileUrl && d.fileName) {
        const node = getNodeById(graph, nodeId);
        if (!node) continue;
        // Only show staff uploads and participant uploads (userForm with actorSource)
        const isStaffNode = node.type === "staffAction" || node.type === "staffApproval";
        const isParticipantNode = node.type === "userForm" && Boolean((node.data as { actorSource?: string }).actorSource);
        if (participantInviteNodeId) {
          // Limited-scope: only staff docs
          if (!isStaffNode) continue;
        } else {
          // Initiator: staff docs + participant docs (not their own uploads like internalRequest)
          if (!isStaffNode && !isParticipantNode) continue;
        }
        const label = (node.data as { label?: string } | undefined)?.label ?? nodeId;
        docs.push({ nodeId, label, fileName: d.fileName as string });
      }
    }
    return docs;
  }, [graph, state.completedPath, allPayloadValues, enterpriseRequestId, participantInviteNodeId]);

  function setValue(key: string, value: unknown): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateUserForm(fields: WizardFieldDef[]): boolean {
    const nextErrors: Record<string, string> = {};
    for (const field of fields) {
      if (!field.required) continue;
      const val = values[field.key];
      if (val === undefined || val === null || val === "") {
        nextErrors[field.key] = t("requests.user.wizard.fieldRequired", { label: field.label });
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(): Promise<void> {
    if (!currentNode) return;
    if (currentNode.type === "userForm") {
      const data = currentNode.data as unknown as UserFormNodeData;
      if (!validateUserForm(data.fields ?? [])) return;
    } else if (currentNode.type === "precondition") {
      if (!validateUserForm(renderFields.filter((f) => (f.type as string) !== 'heading'))) return;
    }
    setSubmitting(true);
    try {
      const submitValues = isPrecondition && hasPerActorFields
        ? { ...values, __actorCount: actorCount }
        : values;
      await onCompleteNode(currentNode.id, submitValues);
      setValues({});
      setErrors({});
    } catch (err) {
      // onCompleteNode threw (e.g. precondition failed, upload error) — keep form values intact
      const message = err instanceof Error ? err.message : String(err);
      if (message && !message.includes("precondition")) {
        toast.error(t("requests.user.wizard.submitError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!currentNode || state.currentNodeId == null) {
    // Walk the traversed path looking for a 'fail' edge. If one exists,
    // the user hit a rejection end node and should see the reason, not
    // a success screen.
    let reachedViaFail = false;
    for (let i = 0; i < state.completedPath.length - 1; i++) {
      const src = state.completedPath[i];
      const dst = state.completedPath[i + 1];
      const edge = graph.edges.find((e) => e.source === src && e.target === dst);
      if (edge?.sourceHandle === "fail") {
        reachedViaFail = true;
        break;
      }
    }

    const endId = state.completedPath[state.completedPath.length - 1];
    const endNode = endId ? getNodeById(graph, endId) : null;
    const endData = endNode?.data as { label?: string; reason?: string } | undefined;
    const endLabel = endData?.label;
    const endReason = endData?.reason;

    if (reachedViaFail) {
      return (
        <div className="space-y-6">
          <WizardStepHeading
            lead={t("requests.user.wizard.workflowRejectedTitle")}
            rest={endLabel || t("requests.user.wizard.workflowRejectedMessage")}
          />
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="flex items-start gap-3 pt-5">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {endLabel || t("requests.user.wizard.workflowRejectedMessage")}
                </p>
                {endReason && (
                  <p className="text-sm text-muted-foreground">{endReason}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Button variant="outline" onClick={onBack}>
            {t("requests.user.wizard.workflowBackToRequests")}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <WizardStepHeading
          lead={t("requests.user.wizard.workflowCompleted")}
          rest={endLabel || t("requests.user.wizard.workflowCompletedMessage")}
        />
        <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
          <CardContent className="flex items-start gap-3 pt-5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            <p className="text-sm font-medium text-foreground">
              {endLabel || t("requests.user.wizard.workflowCompletedMessage")}
            </p>
          </CardContent>
        </Card>
        <Button variant="outline" onClick={onBack}>
          {t("requests.user.wizard.workflowBackToRequests")}
        </Button>
      </div>
    );
  }

  const isUserForm = currentNode.type === "userForm";
  const isPrecondition = currentNode.type === "precondition";
  const isStaffStep =
    currentNode.type === "staffApproval" || currentNode.type === "staffAction";
  const isAutomation = currentNode.type === "automation";
  const isInternalRequest = currentNode.type === "internalRequest";
  const isInviteActors = currentNode.type === "inviteActors";
  const isDecision = currentNode.type === "decision";
  const isDocumentCollection = currentNode.type === "documentCollection";

  // Automation nodes show a waiting card to consumers — only staff can trigger them
  if (isStaffStep || isAutomation) {
    const staff = currentNode.data as unknown as StaffNodeData;
    return (
      <div className="space-y-4">
        {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="flex items-center gap-4 py-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">
                {staff.label || t("requests.user.wizard.workflowWaitingTitle")}
              </h3>
              {staff.description && (
                <p className="text-sm text-muted-foreground">
                  {staff.description}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    );
  }

  if (isInviteActors) {
    return (
      <div className="space-y-4">
        {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

        <InviteActorsNodeView
          nodeData={currentNode.data as InviteActorsNodeData}
          enterpriseRequestId={enterpriseRequestId}
          nodeId={currentNode.id}
          allPayloadValues={allPayloadValues}
          onComplete={async (data) => {
            await onCompleteNode(currentNode.id, data);
          }}
          onBack={!hideBackButton ? onBack : undefined}
        />
      </div>
    );
  }

  if (isDecision) {
    const decisionData = currentNode.data as unknown as DecisionNodeData;
    return (
      <DecisionNodeView
        data={decisionData}
        graph={graph}
        state={state}
        allPayloadValues={allPayloadValues}
        steps={steps}
        hideStepper={hideStepper}
        hideHeading={hideHeading}
        hideBackButton={hideBackButton}
        submitting={submitting}
        onSubmit={async (chosenValue: string) => {
          setSubmitting(true);
          try {
            await onCompleteNode(currentNode.id, { [decisionData.outputKey]: chosenValue });
          } catch {
            // keep state intact
          } finally {
            setSubmitting(false);
          }
        }}
        onBack={onBack}
      />
    );
  }

  if (isDocumentCollection) {
    return (
      <DocumentCollectionNodeView
        nodeData={currentNode.data as unknown as DocumentCollectionNodeData}
        nodeId={currentNode.id}
        enterpriseRequestId={enterpriseRequestId}
        allPayloadValues={allPayloadValues}
        steps={steps}
        hideStepper={hideStepper}
        hideBackButton={hideBackButton}
        onBack={onBack}
        actorIndex={isParticipantStep && isCurrentUserParticipant
          ? (participantsQuery.data?.findIndex(
              (p) => p.userId === user?.id && p.status === "accepted" && p.inviteNodeId === participantActorSource,
            ) ?? 0)
          : 0
        }
      />
    );
  }

  if (isInternalRequest) {
    return (
      <div className="space-y-4">
        {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

        <InternalRequestNodeView
          nodeData={currentNode.data as InternalRequestNodeData}
          enterpriseRequestId={enterpriseRequestId}
          nodeId={currentNode.id}
          childRequestIds={state.childRequestIds}
          actorLabel={getActorLabel(graph)}
          allPayloadValues={allPayloadValues}
        />

        {!hideBackButton && (
          <div className="flex justify-start pt-2">
            <Button variant="outline" onClick={onBack}>
              {t("requests.user.wizard.enterpriseFormBack")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (isParticipantStep && !isCurrentUserParticipant) {
    const sourceNode = participantActorSource
      ? getNodeById(graph, participantActorSource)
      : null;
    const participantLabel = sourceNode
      ? ((sourceNode.data as { actorLabel?: string }).actorLabel?.trim() || 'Participante')
      : 'Participante';
    const participantNodeData = currentNode.data as { label?: string; description?: string };

    return (
      <div className="space-y-4">
        {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardContent className="space-y-4 py-5">
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("requests.user.wizard.waitingForParticipant", { participant: participantLabel })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {participantNodeData.label}
                </p>
              </div>
            </div>

            {/* Participant list with actions */}
            <ParticipantList
              requestId={enterpriseRequestId}
              inviteNodeId={participantActorSource}
              actorLabel={participantLabel}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Limited-scope participant viewing a node that isn't theirs — show completed state
  if (participantInviteNodeId) {
    const currentActorSource = (currentNode.data as { actorSource?: string } | undefined)?.actorSource;
    const isMyNode = currentActorSource === participantInviteNodeId;
    if (!isMyNode) {
      return (
        <div className="space-y-4">
          {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

          <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
            <CardContent className="flex items-center gap-4 py-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("requests.user.wizard.participantStepsComplete")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("requests.user.wizard.participantStepsCompleteHint")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  const nodeData = currentNode.data as unknown as (UserFormNodeData & PreconditionNodeData);
  const maxActors = isPrecondition ? getMaxActors(graph) : 1;
  const actorLabel = isPrecondition ? getActorLabel(graph) : 'Participante';
  const hasPerActorFields = isPrecondition && maxActors > 1 &&
    ((nodeData as unknown as PreconditionNodeData).fields ?? []).some((f) => f.perActor);

  function addActor(): void {
    if (actorCount < maxActors) {
      setActorCount((c) => c + 1);
    }
  }

  function removeActor(): void {
    if (actorCount > 1) {
      // Clear values for the removed actor
      const rawFields = (nodeData as unknown as PreconditionNodeData).fields ?? [];
      const perActorFields = rawFields.filter((f) => f.perActor);
      const removedIdx = actorCount - 1;
      for (const f of perActorFields) {
        const key = `${f.key}_${removedIdx}`;
        setValues((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
      setActorCount((c) => c - 1);
    }
  }

  const renderFields: WizardFieldDef[] = isPrecondition
    ? ((): WizardFieldDef[] => {
        const result: WizardFieldDef[] = [];
        const rawFields = (nodeData as unknown as PreconditionNodeData).fields ?? [];
        const perActorFields = rawFields.filter((f) => f.perActor);
        const sharedFields = rawFields.filter((f) => !f.perActor);

        if (perActorFields.length > 0 && maxActors > 1) {
          for (let actor = 0; actor < actorCount; actor++) {
            result.push({
              key: `__actor_heading_${actor}`,
              label: `${actorLabel} ${actor + 1}`,
              type: 'heading' as WizardFieldDef['type'],
              required: false,
            });
            for (const f of perActorFields) {
              result.push({
                key: `${f.key}_${actor}`,
                label: f.label,
                type: f.type as WizardFieldDef['type'],
                required: true,
                options: f.options ? [...f.options] : undefined,
                suffix: f.suffix, hint: f.hint, maxPercentHint: f.maxPercentHint,
              });
            }
          }
        } else {
          for (const f of perActorFields) {
            result.push({
              key: f.key,
              label: f.label,
              type: f.type as WizardFieldDef['type'],
              required: true,
              options: f.options ? [...f.options] : undefined,
              suffix: f.suffix, hint: f.hint, maxPercentHint: f.maxPercentHint,
            });
          }
        }

        for (const f of sharedFields) {
          result.push({
            key: f.key,
            label: f.label,
            type: f.type as WizardFieldDef['type'],
            required: true,
            options: f.options ? [...f.options] : undefined,
            suffix: f.suffix, hint: f.hint, maxPercentHint: f.maxPercentHint,
          });
        }

        return result;
      })()
    : (nodeData.fields as WizardFieldDef[]) ?? [];

  return (
    <div className="space-y-4">
      {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

      {/* Pricing recap sits above the form on any node that follows a
          pricing step (typically the contact form); for the precondition
          step it renders live beneath the calculator fields.
          Hidden for limited-scope participants. */}
      {isUserForm && !participantInviteNodeId && !(nodeData.fields ?? []).every((f) => f.type === 'file') && (
        <LiveGraphPricingCard
          graph={graph}
          state={state}
          currentValues={values}
          previousValues={allPayloadValues}
        />
      )}

      {/* Documents from completed steps — show download links */}
      {completedDocuments.length > 0 && enterpriseRequestId && (
        <Card className="border-blue-200/60 bg-blue-50/30 dark:border-blue-900 dark:bg-blue-950/20">
          <CardContent className="space-y-3 py-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <FileDown className="h-4 w-4 text-blue-600" />
              {t("requests.user.wizard.availableDocuments")}
            </h4>
            <div className="space-y-2">
              {completedDocuments.map((doc) => (
                <div key={doc.nodeId} className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{doc.fileName}</p>
                  </div>
                  <AuthenticatedBlobDocumentLink
                    fetchBlob={() => fetchMyEnterpriseDocumentBlob(enterpriseRequestId, doc.nodeId)}
                    title={doc.fileName}
                    className="shrink-0 rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-foreground no-underline shadow-sm hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <FileDown className="h-3.5 w-3.5" />
                      {t("requests.user.wizard.downloadDocument")}
                    </span>
                  </AuthenticatedBlobDocumentLink>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* When on a detail page (hideBackButton), heading goes inside the card.
          In the wizard flow, heading stays outside as a large title. */}
      {!hideHeading && !hideBackButton && (
        <WizardStepHeading
          lead={nodeData.label || t("requests.user.wizard.workflowSubmitStep")}
          rest={nodeData.description ?? ""}
        />
      )}

      {hasPerActorFields ? (
        <>
          {/* Per-actor fields in their own card */}
          <Card>
            <CardContent className="space-y-4 pt-5">
              {!hideHeading && hideBackButton && (
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    {nodeData.label || t("requests.user.wizard.workflowSubmitStep")}
                  </h3>
                  {nodeData.description && (
                    <p className="text-sm text-muted-foreground">{nodeData.description}</p>
                  )}
                </div>
              )}
              <GraphFormFields
                fields={renderFields.filter((f) => f.key.startsWith('__actor_heading_') || /_\d+$/.test(f.key))}
                values={values}
                errors={errors}
                onValueChange={setValue}
              />
              <div className="flex items-center gap-2 pt-2">
                {actorCount < maxActors && (
                  <Button type="button" variant="outline" size="sm" onClick={addActor}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Agregar {actorLabel.toLowerCase()}
                  </Button>
                )}
                {actorCount > 1 && (
                  <Button type="button" variant="ghost-destructive" size="sm" onClick={removeActor}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Quitar último
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shared fields in a separate card */}
          {renderFields.some((f) => !f.key.startsWith('__actor_heading_') && !/_\d+$/.test(f.key)) && (
            <Card>
              <CardContent className="space-y-4 pt-5">
                <GraphFormFields
                  fields={renderFields.filter((f) => !f.key.startsWith('__actor_heading_') && !/_\d+$/.test(f.key))}
                  values={values}
                  errors={errors}
                  onValueChange={setValue}
                />
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="space-y-4 pt-5">
            {!hideHeading && hideBackButton && (
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-foreground">
                  {nodeData.label || t("requests.user.wizard.workflowSubmitStep")}
                </h3>
                {nodeData.description && (
                  <p className="text-sm text-muted-foreground">{nodeData.description}</p>
                )}
              </div>
            )}
            {(isUserForm || isPrecondition) && (
              <GraphFormFields
                fields={renderFields}
                values={values}
                errors={errors}
                onValueChange={setValue}
              />
            )}
            {/* Submit button inside card on detail page */}
            {hideBackButton && (isUserForm || isPrecondition) && (() => {
              const hasFileFields = isUserForm && (nodeData.fields ?? []).some((f) => f.type === "file");
              const buttonLabel = submitting
                ? t("requests.user.wizard.workflowSubmittingStep")
                : hasFileFields
                  ? t("requests.user.wizard.uploadAndContinue")
                  : progress.total > 1 && progress.current < progress.total
                    ? t("requests.user.wizard.workflowNextStep")
                    : t("requests.user.wizard.workflowSubmitStep");
              return (
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSubmit} disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {buttonLabel}
                  </Button>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {isPrecondition && (
        <LiveGraphPricingCard
          graph={graph}
          state={state}
          currentValues={values}
          previousValues={allPayloadValues}
        />
      )}

      <ChildRequestActions
        currentNodeId={currentNode.id}
        enterpriseRequestId={enterpriseRequestId}
        childRequestIds={state.childRequestIds}
        onStartChildRequest={(requestType) => {
          const params = new URLSearchParams({
            returnEnterpriseRequest: enterpriseRequestId,
            returnStep: currentNode.id,
            ...(requestType ? { requestType } : {}),
          });
          navigate(`/requests/new?${params.toString()}`);
        }}
      />

      {/* Back/Submit buttons — only show outer buttons in wizard mode (not detail page) */}
      {!hideBackButton && (
        <div className="flex pt-2 justify-between">
          <Button variant="outline" onClick={onBack} disabled={submitting}>
            {t("requests.user.wizard.enterpriseFormBack")}
          </Button>
          {(isUserForm || isPrecondition) && (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("requests.user.wizard.workflowSubmittingStep")}
                </>
              ) : progress.total > 1 && progress.current < progress.total ? (
                t("requests.user.wizard.workflowNextStep")
              ) : !enterpriseRequestId ? (
                t("requests.user.wizard.workflowNextStep")
              ) : (
                t("requests.user.wizard.workflowSubmitStep")
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ParticipantList({
  requestId,
  inviteNodeId,
  actorLabel,
}: {
  requestId: string;
  inviteNodeId: string | null;
  actorLabel: string;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const participantsQuery = useQuery({
    queryKey: ["enterprise-request-participants-manage", requestId],
    queryFn: () => getEnterpriseRequestParticipants(requestId),
  });

  const resendMutation = useMutation({
    mutationFn: (participantId: string) => resendParticipantInvite(requestId, participantId),
    onSuccess: () => toast.success(t("requests.user.invites.resendSuccess")),
    onError: () => toast.error(t("requests.user.invites.resendError")),
  });

  const cancelMutation = useMutation({
    mutationFn: (participantId: string) => cancelParticipantInvite(requestId, participantId),
    onSuccess: () => {
      toast.success(t("requests.user.invites.cancelSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["enterprise-request-participants-manage", requestId] });
    },
    onError: () => toast.error(t("requests.user.invites.cancelError")),
  });

  const participants = (participantsQuery.data ?? []).filter(
    (p) => p.role !== "initiator" && (!inviteNodeId || p.inviteNodeId === inviteNodeId),
  );

  if (participantsQuery.isLoading) {
    return <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />;
  }

  if (participants.length === 0) return <></>;

  return (
    <div className="space-y-2 border-t border-amber-200 dark:border-amber-800 pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("requests.user.invites.title")}
      </p>
      {participants.map((p) => {
        const isPending = p.status === "pending";
        const isAccepted = p.status === "accepted";
        const displayName = p.user
          ? [p.user.firstName, p.user.lastName].filter(Boolean).join(" ").trim() || p.email
          : p.email;

        return (
          <div
            key={p.id}
            className="flex items-center gap-3 rounded-lg border border-amber-100 bg-white/60 dark:border-amber-800/50 dark:bg-amber-950/20 p-2.5"
          >
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              isAccepted
                ? "bg-green-100 dark:bg-green-900/30"
                : "bg-amber-50 dark:bg-amber-900/20"
            }`}>
              {isAccepted ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Clock className="h-3.5 w-3.5 text-amber-500" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">{actorLabel}</span>
                <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                  isAccepted
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                }`}>
                  {isAccepted
                    ? t("requests.user.invites.statusAccepted")
                    : t("requests.user.invites.statusPending")}
                </span>
              </div>
            </div>

            {isPending && (
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => resendMutation.mutate(p.id)}
                  disabled={resendMutation.isPending}
                  title={t("requests.user.invites.resend")}
                >
                  {resendMutation.isPending && resendMutation.variables === p.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  variant="ghost-destructive"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => cancelMutation.mutate(p.id)}
                  disabled={cancelMutation.isPending}
                  title={t("requests.user.invites.cancel")}
                >
                  {cancelMutation.isPending && cancelMutation.variables === p.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}






function GraphFormFields({
  fields,
  values,
  errors,
  onValueChange,
}: {
  fields: WizardFieldDef[];
  values: Record<string, unknown>;
  errors: Record<string, string>;
  onValueChange: (key: string, value: unknown) => void;
}): JSX.Element {
  return (
    <>
      {fields.map((field) => {
        if ((field.type as string) === 'heading') {
          return (
            <div key={field.key} className="pt-3 first:pt-0">
              <h4 className="text-sm font-semibold text-foreground">{field.label}</h4>
            </div>
          );
        }
        return (
        <div key={field.key} className="space-y-1.5">
          <Label htmlFor={field.key}>
            {field.label}
            {field.required && <span className="ml-0.5 text-destructive">*</span>}
          </Label>

          {field.type === "text" && (
            <Input
              id={field.key}
              value={(values[field.key] as string) ?? ""}
              onChange={(e) => onValueChange(field.key, e.target.value)}
            />
          )}

          {field.type === "email" && (
            <Input
              id={field.key}
              type="email"
              value={(values[field.key] as string) ?? ""}
              onChange={(e) => onValueChange(field.key, e.target.value)}
            />
          )}

          {(field.type === "number" || field.type === "currency") && (
            <div className="relative">
              {(field.type === "currency" || field.suffix) && (
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                  {field.type === "currency"
                    ? "$"
                    : field.suffix === "UYU" || field.suffix === "USD"
                      ? "$"
                      : field.suffix}
                </span>
              )}
              <Input
                id={field.key}
                type="number"
                className={field.type === "currency" || field.suffix ? "pl-8" : undefined}
                value={(values[field.key] as string) ?? ""}
                onChange={(e) =>
                  onValueChange(field.key, e.target.value === "" ? "" : Number(e.target.value))
                }
              />
            </div>
          )}

          {field.type === "textarea" && (
            <Textarea
              id={field.key}
              value={(values[field.key] as string) ?? ""}
              onChange={(e) => onValueChange(field.key, e.target.value)}
              rows={3}
            />
          )}

          {field.type === "select" && (
            <Select
              value={(values[field.key] as string) ?? ""}
              onValueChange={(v) => onValueChange(field.key, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={field.label} />
              </SelectTrigger>
              <SelectContent>
                {(field.options ?? []).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {field.type === "checkbox" && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.key}
                checked={!!values[field.key]}
                onCheckedChange={(v) => onValueChange(field.key, v === true)}
              />
              <Label htmlFor={field.key} className="font-normal">
                {field.label}
              </Label>
            </div>
          )}

          {field.type === "date" && (
            <DatePickerField
              id={field.key}
              label={null}
              value={(values[field.key] as string) ?? ""}
              onChange={(v) => onValueChange(field.key, v)}
              placeholder="dd/mm/aaaa"
            />
          )}

          {field.type === "file" && (
            <StyledFileInput
              id={field.key}
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              value={(values[`__file_${field.key}`] as File) ?? null}
              onChange={(file) => {
                onValueChange(field.key, file ? file.name : "");
                onValueChange(`__file_${field.key}`, file);
              }}
              hint="PDF, JPG, PNG (máx. 10 MB)"
            />
          )}

          {errors[field.key] && (
            <p className="text-xs text-destructive">{errors[field.key]}</p>
          )}
          {field.hint && !errors[field.key] && (
            <p className="text-xs text-muted-foreground">{field.hint}</p>
          )}
          {field.maxPercentHint && !errors[field.key] && (
            <MaxPercentHint
              values={values}
              refField={field.maxPercentHint.field}
              percent={field.maxPercentHint.percent}
            />
          )}
        </div>
        );
      })}
    </>
  );
}

function ChildRequestActions({
  currentNodeId,
  enterpriseRequestId: _enterpriseRequestId,
  childRequestIds,
  onStartChildRequest,
}: {
  currentNodeId: string;
  enterpriseRequestId: string;
  childRequestIds: Record<string, string>;
  onStartChildRequest: (requestType?: string) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const linkedChildId = childRequestIds[currentNodeId];
  void onStartChildRequest;

  if (!linkedChildId) return null;

  return (
    <Card>
      <CardContent className="flex items-center gap-2 py-3">
        <CheckCircle2 className="h-5 w-5 text-green-600" />
        <span className="text-sm text-foreground">
          {t("requests.user.wizard.workflowInternalRequestLinked")}
        </span>
        <ExternalLink className="ml-auto h-4 w-4 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}

function LiveGraphPricingCard({
  graph,
  state,
  currentValues,
  previousValues,
}: {
  graph: ProductGraph;
  state: GraphState;
  currentValues: Record<string, unknown>;
  previousValues: Record<string, unknown>;
}): JSX.Element | null {
  const { t } = useTranslation();

  const pricingNodeOnPath = useMemo(() => {
    for (const id of state.completedPath) {
      const node = getNodeById(graph, id);
      if (node?.type === "pricing") return node;
    }
    return null;
  }, [graph, state.completedPath]);

  if (!pricingNodeOnPath) return null;

  const formula = (pricingNodeOnPath.data as unknown as PricingNodeData).formula;

  const merged: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(previousValues ?? {})) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(merged, value);
    } else {
      merged[key] = value;
    }
  }
  Object.assign(merged, currentValues);

  const allBaseFieldsPresent = formula.baseFields.every((key) => {
    const v = Number(merged[key]);
    return Number.isFinite(v) && v > 0;
  });
  const profilePresent = Boolean(merged[formula.profileField]);

  if (!allBaseFieldsPresent || !profilePresent) return null;

  const result = evaluateRateTable(formula, merged);
  if ("error" in result) return null;

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="space-y-2 pt-4">
        <h4 className="text-sm font-semibold text-foreground">
          {t("requests.user.wizard.pricingSummaryTitle")}
        </h4>
        <div className="space-y-1">
          {result.breakdown.map((line, idx) => {
            const isLast = idx === result.breakdown.length - 1;
            return (
              <div
                key={idx}
                className={`flex items-center justify-between text-sm ${
                  isLast
                    ? "font-semibold text-foreground border-t pt-1 mt-1"
                    : "text-muted-foreground"
                }`}
              >
                <span>{line.label}</span>
                <span>{line.value}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


function WorkflowStepper({
  steps,
}: {
  steps: Array<{ id: string; label: string; status: "completed" | "current" | "upcoming" }>;
}): JSX.Element {
  return (
    <nav aria-label="Workflow progress" className="overflow-x-auto pb-2 -mx-1 px-1">
      <ol className="flex items-center gap-0 min-w-max">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          return (
            <li
              key={step.id}
              className={`flex items-center ${isLast ? "" : "flex-1"}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {step.status === "completed" ? (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                ) : step.status === "current" ? (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-background">
                    <Circle className="h-2.5 w-2.5 fill-primary text-primary" />
                  </span>
                ) : (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground/30 bg-background">
                    <Circle className="h-2.5 w-2.5 fill-muted-foreground/30 text-muted-foreground/30" />
                  </span>
                )}
                <span
                  className={`text-xs font-medium truncate ${
                    step.status === "completed"
                      ? "text-foreground"
                      : step.status === "current"
                        ? "text-primary font-semibold"
                        : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div
                  className={`mx-2 h-px flex-1 min-w-4 ${
                    step.status === "completed"
                      ? "bg-primary"
                      : "bg-muted-foreground/30"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function DecisionNodeView({
  data,
  graph,
  state,
  allPayloadValues,
  steps,
  hideStepper,
  hideHeading,
  hideBackButton,
  submitting,
  onSubmit,
  onBack,
}: {
  data: DecisionNodeData;
  graph: ProductGraph;
  state: GraphState;
  allPayloadValues: Record<string, unknown>;
  steps: Array<{ id: string; label: string; status: "completed" | "current" | "upcoming" }>;
  hideStepper: boolean;
  hideHeading: boolean;
  hideBackButton: boolean;
  submitting: boolean;
  onSubmit: (value: string) => Promise<void>;
  onBack: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  // Build context data from displayFrom node
  const displayData = useMemo(() => {
    if (!data.displayFrom) return null;
    const record = state.completedNodes[data.displayFrom];
    if (!record?.data) return null;
    return record.data;
  }, [data.displayFrom, state.completedNodes]);

  // If displayFrom is a pricing node, compute the breakdown
  const pricingBreakdown = useMemo(() => {
    if (!data.displayFrom) return null;
    const displayNode = getNodeById(graph, data.displayFrom);
    if (!displayNode || displayNode.type !== "pricing") return null;
    const formula = (displayNode.data as unknown as PricingNodeData).formula;

    const merged: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(allPayloadValues ?? {})) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.assign(merged, value);
      } else {
        merged[key] = value;
      }
    }

    const result = evaluateRateTable(formula, merged);
    if ("error" in result) return null;
    return result;
  }, [data.displayFrom, graph, allPayloadValues]);

  return (
    <div className="space-y-4">
      {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

      {!hideHeading && (
        <WizardStepHeading
          lead={data.label}
          rest={data.description ?? ""}
        />
      )}

      {pricingBreakdown && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="space-y-2 pt-4">
            <h4 className="text-sm font-semibold text-foreground">
              {t("requests.user.wizard.pricingSummaryTitle")}
            </h4>
            <div className="space-y-1">
              {pricingBreakdown.breakdown.map((line, idx) => {
                const isLast = idx === pricingBreakdown.breakdown.length - 1;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between text-sm ${
                      isLast
                        ? "font-semibold text-foreground border-t pt-1 mt-1"
                        : "text-muted-foreground"
                    }`}
                  >
                    <span>{line.label}</span>
                    <span>{line.value}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {!pricingBreakdown && displayData && (
        <Card className="border-muted">
          <CardContent className="space-y-1 pt-4">
            {Object.entries(displayData).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{key}</span>
                <span className="text-foreground">{String(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.options.length > 2 && (
        <div className="space-y-2">
          {data.options.map((opt) => (
            <Card
              key={opt.value}
              className={`cursor-pointer transition-all ${
                selected === opt.value
                  ? "border-primary ring-2 ring-primary/20"
                  : "hover:border-primary/40"
              }`}
              onClick={() => setSelected(opt.value)}
            >
              <CardContent className="flex items-center gap-3 py-3">
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    selected === opt.value
                      ? "border-primary bg-primary"
                      : "border-muted-foreground/30"
                  }`}
                >
                  {selected === opt.value && (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{opt.label}</p>
                  {opt.description && (
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data.options.length <= 2 ? (
        <div className="flex justify-between pt-2">
          {data.options.length === 2 && (
            <Button
              variant="outline"
              onClick={() => onSubmit(data.options[1].value)}
              disabled={submitting}
            >
              {data.options[1].label}
            </Button>
          )}
          <Button
            onClick={() => onSubmit(data.options[0].value)}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("requests.user.wizard.workflowSubmittingStep")}
              </>
            ) : (
              data.options[0].label
            )}
          </Button>
        </div>
      ) : (
        <div className={`flex pt-2 ${hideBackButton ? "justify-end" : "justify-between"}`}>
          {!hideBackButton && (
            <Button variant="outline" onClick={onBack} disabled={submitting}>
              {t("requests.user.wizard.enterpriseFormBack")}
            </Button>
          )}
          <Button
            onClick={() => selected && onSubmit(selected)}
            disabled={submitting || !selected}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("requests.user.wizard.workflowSubmittingStep")}
              </>
            ) : (
              t("requests.user.wizard.workflowNextStep")
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function MaxPercentHint({
  values,
  refField,
  percent,
}: {
  values: Record<string, unknown>;
  refField: string;
  percent: number;
}): JSX.Element | null {
  // Sum the reference field — handles both single (nominalIncome) and
  // per-actor indexed keys (nominalIncome_0, nominalIncome_1, ...)
  let total = Number(values[refField]) || 0;
  if (total === 0) {
    for (const [key, val] of Object.entries(values)) {
      if (key.startsWith(`${refField}_`)) {
        total += Number(val) || 0;
      }
    }
  }
  if (total <= 0) return null;

  const max = total * (percent / 100);
  const formatted = max.toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return (
    <p className="text-xs text-muted-foreground">
      Máximo: <span className="font-medium">${formatted}</span>
    </p>
  );
}

function DocumentCollectionNodeView({
  nodeData,
  nodeId,
  enterpriseRequestId,
  allPayloadValues,
  steps,
  hideStepper,
  hideBackButton,
  onBack,
  actorIndex,
}: {
  nodeData: DocumentCollectionNodeData;
  nodeId: string;
  enterpriseRequestId: string;
  allPayloadValues: Record<string, unknown>;
  steps: { id: string; label: string; status: "completed" | "current" | "upcoming" }[];
  hideStepper?: boolean;
  hideBackButton?: boolean;
  onBack?: () => void;
  actorIndex: number;
}): JSX.Element {
  const { t } = useTranslation();
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  // Determine which document set applies to this actor.
  // Per-actor precondition fields are always stored as `key_N` (e.g. profile_0),
  // so try the actor-indexed key first, then the base key as fallback.
  const keysToTry = [
    `${nodeData.conditionField}_${actorIndex}`,
    nodeData.conditionField,
  ];
  // Payload is nested by node ID (e.g. { eligibility: { profile_0: "..." } }),
  // so search through all node payloads to find the field value.
  let conditionValue = "";
  outer: for (const key of keysToTry) {
    for (const nodePayload of Object.values(allPayloadValues)) {
      if (nodePayload && typeof nodePayload === "object" && !Array.isArray(nodePayload)) {
        const val = (nodePayload as Record<string, unknown>)[key];
        if (val != null) {
          conditionValue = String(val);
          break outer;
        }
      }
    }
  }
  const docSet = nodeData.documentSets[conditionValue];

  // Get already-uploaded docs from payload
  const payloadKey = nodeData.actorSource ? `${nodeId}_${actorIndex}` : nodeId;
  const uploadedDocs = (allPayloadValues[payloadKey] ?? {}) as Record<
    string,
    { fileUrl?: string; fileName?: string }
  >;

  const requiredDocs = docSet?.documents?.filter((d) => d.required) ?? [];
  const allDocs = docSet?.documents ?? [];
  const uploadedCount = requiredDocs.filter((d) => uploadedDocs[d.key]?.fileUrl).length;
  const allRequiredDone = uploadedCount === requiredDocs.length;

  const handleUpload = async (documentKey: string, file: File) => {
    const actorNodeId = nodeData.actorSource ? `${nodeId}_${actorIndex}` : nodeId;
    setUploadingKey(documentKey);
    try {
      await uploadMyCollectionDocument(enterpriseRequestId, actorNodeId, documentKey, file);
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
    } catch {
      toast.error(t("requests.user.wizard.enterpriseFormError"));
    } finally {
      setUploadingKey(null);
    }
  };

  if (!docSet) {
    return (
      <div className="space-y-4">
        {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t("requests.user.wizard.documentCollectionNoProfile")}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideStepper && steps.length > 1 && <WorkflowStepper steps={steps} />}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-purple-600" />
            {nodeData.label || t("requests.user.wizard.documentCollectionTitle")}
          </CardTitle>
          {nodeData.description && (
            <p className="text-sm text-muted-foreground">{nodeData.description}</p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">{docSet.label}</span>
            <Badge variant={allRequiredDone ? "default" : "secondary"} className="text-[10px]">
              {uploadedCount}/{requiredDocs.length}
            </Badge>
          </div>
          {docSet.description && (
            <p className="text-xs text-muted-foreground">{docSet.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {allDocs.map((doc) => {
            const uploaded = uploadedDocs[doc.key];
            const isUploading = uploadingKey === doc.key;

            return (
              <div
                key={doc.key}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-3 transition-colors",
                  uploaded?.fileUrl ? "border-green-200 bg-green-50/50" : "border-border",
                )}
              >
                {uploaded?.fileUrl ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-muted-foreground/40" />
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{doc.label}</span>
                  {doc.required && !uploaded?.fileUrl && (
                    <span className="ml-1 text-xs text-destructive">*</span>
                  )}
                  {uploaded?.fileName && (
                    <p className="text-xs text-muted-foreground truncate">
                      {uploaded.fileName}
                    </p>
                  )}
                </div>
                <label className="cursor-pointer shrink-0">
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleUpload(doc.key, file);
                      e.target.value = "";
                    }}
                    disabled={isUploading}
                  />
                  <span className={cn(
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    uploaded?.fileUrl
                      ? "border border-input bg-background text-foreground hover:bg-accent"
                      : "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}>
                    {isUploading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    {uploaded?.fileUrl
                      ? t("requests.user.wizard.documentCollectionChange")
                      : t("requests.user.wizard.documentCollectionUpload")}
                  </span>
                </label>
              </div>
            );
          })}
          <div className="flex justify-end pt-3">
            <Button
              disabled={!allRequiredDone || submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  await completeMyEnterpriseRequestStep(enterpriseRequestId, nodeId, {});
                  void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
                } catch {
                  toast.error(t("requests.user.wizard.enterpriseFormError"));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("requests.user.wizard.enterpriseFormContinue")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!hideBackButton && (
        <div className="flex justify-start pt-2">
          <Button variant="outline" onClick={onBack}>
            {t("requests.user.wizard.enterpriseFormBack")}
          </Button>
        </div>
      )}
    </div>
  );
}
